import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, fmt, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Building2, Calendar, TrendingUp, ShieldCheck, ShieldAlert, ExternalLink, Package, AlertTriangle, PiggyBank } from "lucide-react";

function fom() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`; }
function today() { return new Date().toISOString().slice(0, 10); }

export default function VendorDashboard() {
    const { id } = useParams();
    const [start, setStart] = useState(fom());
    const [end, setEnd] = useState(today());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/vendors/${id}/dashboard`, { params: { start, end } })
            .then((r) => setData(r.data))
            .catch((e) => toast.error(e.response?.data?.detail || "Failed to load"))
            .finally(() => setLoading(false));
    }, [id, start, end]);

    const presets = [
        ["This month", fom(), today()],
        ["Last 30 days", new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10), today()],
        ["This FY", (() => { const d = new Date(); const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear()-1; return `${y}-04-01`; })(), today()],
        ["All time", "", today()],
    ];

    if (!data) return <div className="p-6 text-slate-500">{loading ? "Loading…" : "No data"}</div>;
    const v = data.vendor;
    const k = data.kpis;
    const maxMonthSpend = Math.max(...data.monthly.map((m) => m.spent), 1);

    return (
        <div data-testid="vendor-dashboard">
            {/* Header */}
            <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
                <div>
                    <Link to="/vendors" className="text-xs text-slate-500 hover:text-slate-900 mb-1 inline-flex items-center gap-1">
                        <ArrowLeft size={12} /> Back to vendors
                    </Link>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900 flex items-center gap-3">
                        <Building2 size={26} className="text-slate-600" />
                        {v.name}
                        {k.has_gstin
                            ? <span className="hdb-tag hdb-tag-mint text-xs"><ShieldCheck size={10} className="inline mr-1"/>ITC eligible</span>
                            : <span className="hdb-tag hdb-tag-gold text-xs"><ShieldAlert size={10} className="inline mr-1"/>No GSTIN — no ITC</span>}
                    </h1>
                    <div className="text-sm text-slate-500 mt-1 hdb-mono">{v.gstin || "—"} · {v.state || "—"} · {v.contact || "—"}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="hdb-card p-4 mb-5 flex flex-wrap gap-4 items-end">
                <div>
                    <label className="hdb-label"><Calendar size={11} className="inline mr-1" /> From</label>
                    <input type="date" className="hdb-input hdb-mono" value={start} onChange={(e) => setStart(e.target.value)} data-testid="vd-start" />
                </div>
                <div>
                    <label className="hdb-label">To</label>
                    <input type="date" className="hdb-input hdb-mono" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="vd-end" />
                </div>
                <div className="flex flex-wrap gap-2 ml-auto">
                    {presets.map(([label, s, e]) => (
                        <button key={label} className="hdb-btn hdb-btn-ghost text-xs"
                                onClick={() => { setStart(s); setEnd(e); }}>{label}</button>
                    ))}
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                <KPI label="Purchases" value={k.count} icon={Package} />
                <KPI label="Total spent" value={fmt(k.total_spent)} tone="slate" />
                <KPI label="ITC available" value={fmt(k.total_itc)} tone={k.has_gstin ? "mint" : "red"}
                     hint={k.has_gstin ? `${k.itc_pct}% of spend` : "not claimable"} />
                <KPI label="Avg bill" value={fmt(k.avg_bill)} tone="slate" />
                <KPI label="Buying cadence" value={k.days_between_bills != null ? `${k.days_between_bills} days` : "—"}
                     hint="between bills" tone="slate" />
            </div>

            {/* Two-column: Tax by slab + Monthly trend */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                <div className="hdb-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="hdb-heading font-bold text-slate-900">Input Tax Credit by GST slab</div>
                        <PiggyBank size={16} className="text-emerald-600" />
                    </div>
                    <table className="hdb-table w-full">
                        <thead><tr><th>Rate</th><th className="text-right">Taxable</th>
                            <th className="text-right">CGST</th><th className="text-right">SGST</th>
                            <th className="text-right">IGST</th><th className="text-right">Total ITC</th></tr></thead>
                        <tbody>
                            {data.by_slab.map((s) => (
                                <tr key={s.rate} data-testid={`slab-${s.rate}`}>
                                    <td className="hdb-mono font-semibold">{s.rate}%</td>
                                    <td className="hdb-num hdb-num-right">{fmt(s.taxable)}</td>
                                    <td className="hdb-num hdb-num-right">{fmt(s.cgst)}</td>
                                    <td className="hdb-num hdb-num-right">{fmt(s.sgst)}</td>
                                    <td className="hdb-num hdb-num-right">{fmt(s.igst)}</td>
                                    <td className="hdb-num hdb-num-right font-bold text-emerald-700">{fmt(s.total_tax)}</td>
                                </tr>
                            ))}
                            {data.by_slab.length === 0 && (
                                <tr><td colSpan={6} className="text-center py-6 text-slate-400">No taxable purchases in this period</td></tr>
                            )}
                        </tbody>
                    </table>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between text-sm">
                        <span className="text-slate-500">Total input tax</span>
                        <span className="hdb-num font-black text-emerald-700 text-lg">{fmt(k.total_itc)}</span>
                    </div>
                </div>

                <div className="hdb-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="hdb-heading font-bold text-slate-900">Monthly purchase trend</div>
                        <TrendingUp size={16} className="text-slate-600" />
                    </div>
                    <div className="space-y-2">
                        {data.monthly.map((m) => (
                            <div key={m.bucket} className="flex items-center gap-3">
                                <div className="text-xs hdb-mono text-slate-500 w-20">{m.bucket}</div>
                                <div className="flex-1 h-6 bg-slate-50 rounded overflow-hidden relative">
                                    <div className="h-full bg-slate-900" style={{ width: `${(m.spent / maxMonthSpend) * 100}%` }}></div>
                                    <div className="absolute inset-0 flex items-center px-2 text-xs font-bold text-white">
                                        {fmt(m.spent)}
                                    </div>
                                </div>
                                <div className="text-xs text-slate-500 w-16 text-right">{m.count} bill{m.count !== 1 ? "s" : ""}</div>
                            </div>
                        ))}
                        {data.monthly.length === 0 && (
                            <div className="text-slate-400 text-sm text-center py-6">No data</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Top items + Rate volatility */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                <div className="hdb-card p-5">
                    <div className="hdb-heading font-bold text-slate-900 mb-3">Top items purchased</div>
                    <table className="hdb-table w-full">
                        <thead><tr><th>#</th><th>Item</th><th className="text-right">Qty</th>
                            <th className="text-right">Spent</th><th className="text-right">Bills</th></tr></thead>
                        <tbody>
                            {data.top_items.map((it, i) => (
                                <tr key={it.name}>
                                    <td className="hdb-mono text-xs">{i + 1}</td>
                                    <td className="font-medium">{it.name}</td>
                                    <td className="hdb-num hdb-num-right">{fmtNum(it.qty)}</td>
                                    <td className="hdb-num hdb-num-right font-bold">{fmt(it.spent)}</td>
                                    <td className="hdb-num hdb-num-right text-slate-500">{it.invoice_count}</td>
                                </tr>
                            ))}
                            {data.top_items.length === 0 && (
                                <tr><td colSpan={5} className="text-center py-6 text-slate-400">No line items</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="hdb-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="hdb-heading font-bold text-slate-900">Rate volatility (price hikes)</div>
                        <AlertTriangle size={16} className="text-amber-600" />
                    </div>
                    <table className="hdb-table w-full">
                        <thead><tr><th>Item</th><th className="text-right">Min</th>
                            <th className="text-right">Max</th><th className="text-right">Change</th>
                            <th className="text-right">Samples</th></tr></thead>
                        <tbody>
                            {data.rate_volatility.map((r) => (
                                <tr key={r.name}>
                                    <td className="font-medium">{r.name}</td>
                                    <td className="hdb-num hdb-num-right">{fmt(r.min)}</td>
                                    <td className="hdb-num hdb-num-right">{fmt(r.max)}</td>
                                    <td className={`hdb-num hdb-num-right font-bold ${r.change_pct >= 10 ? "text-red-600" : r.change_pct >= 3 ? "text-amber-600" : "text-slate-500"}`}>
                                        {r.change_pct > 0 ? `+${r.change_pct}%` : "—"}
                                    </td>
                                    <td className="hdb-num hdb-num-right text-slate-500">{r.samples}</td>
                                </tr>
                            ))}
                            {data.rate_volatility.length === 0 && (
                                <tr><td colSpan={5} className="text-center py-6 text-slate-400">Need ≥ 2 bills for the same item to compare rates</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Recent purchases */}
            <div className="hdb-card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 hdb-heading font-bold text-slate-900">
                    Purchases in this period ({data.purchases.length})
                </div>
                <table className="hdb-table w-full">
                    <thead><tr><th>Bill #</th><th>Date</th>
                        <th className="text-right">Subtotal</th><th className="text-right">Tax</th>
                        <th className="text-right">Grand</th><th></th></tr></thead>
                    <tbody>
                        {data.purchases.map((p) => (
                            <tr key={p.id}>
                                <td className="hdb-mono font-semibold">{p.bill_number || "—"}</td>
                                <td className="hdb-mono">{p.bill_date}</td>
                                <td className="hdb-num hdb-num-right">{fmt(p.subtotal)}</td>
                                <td className="hdb-num hdb-num-right text-emerald-700">{fmt(p.total_tax)}</td>
                                <td className="hdb-num hdb-num-right font-bold">{fmt(p.grand_total)}</td>
                                <td className="text-right">
                                    {p.has_attachment && (
                                        <a href={`${API}/purchases/${p.id}/attachment`} target="_blank" rel="noreferrer"
                                           className="hdb-btn hdb-btn-ghost" title="View original bill">
                                            <ExternalLink size={12} />
                                        </a>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {data.purchases.length === 0 && (
                            <tr><td colSpan={6} className="text-center py-10 text-slate-400">No purchases in this period</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function KPI({ label, value, tone = "slate", icon: Icon, hint }) {
    const tones = { slate: "text-slate-900", mint: "text-emerald-700", gold: "text-amber-700", red: "text-red-700" };
    return (
        <div className="hdb-card p-4">
            <div className="hdb-label flex items-center gap-1">{Icon && <Icon size={11} />} {label}</div>
            <div className={`hdb-num text-2xl font-black mt-1 ${tones[tone]}`}>{value}</div>
            {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
        </div>
    );
}
